import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { DataGrid } from '../components/DataGrid';
import { AlertActions } from '../components/itops/AlertActions';
import { Pulse, Warning, CheckCircle, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

const Dot = ({ status }) => {
  const c = { healthy: 'bg-green-500', configured: 'bg-sky-400', stubbed: 'bg-amber-400', idle: 'bg-slate-300', error: 'bg-rose-500' }[status] || 'bg-slate-300';
  return <span className={`inline-block w-2 h-2 rounded-full ${c}`} />;
};

const Stat = ({ label, value, tone, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
    <p className="font-mono-label text-[10px] uppercase text-slate-400">{label}</p>
    <p className={`font-display text-2xl font-extrabold ${tone || ''}`}>{value}</p>
  </div>
);

export default function ITOps() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canTicket = ['global_admin', 'school_admin'].includes(user?.role);
  const { data, isLoading } = useQuery({ queryKey: ['itops'], queryFn: () => api.get('/itops/overview').then((r) => r.data), refetchInterval: 30000 });
  const { data: connectors } = useQuery({ queryKey: ['itsm-connectors'], queryFn: () => api.get('/itsm/connectors').then((r) => r.data), enabled: canTicket });
  const { data: tickets } = useQuery({ queryKey: ['itsm-tickets'], queryFn: () => api.get('/itsm/tickets').then((r) => r.data), enabled: canTicket });
  const retryItsm = async (id) => {
    try { await api.post(`/itsm/tickets/${id}/retry`); toast.success('Retry executed'); qc.invalidateQueries({ queryKey: ['itops'] }); } catch (e) { toast.error(e?.response?.data?.detail || 'Retry failed'); }
  };
  if (isLoading || !data) return <p className="text-sm text-slate-400 p-6" data-testid="itops-loading">Loading operational picture...</p>;
  const p = data.pending || {};

  return (
    <div className="space-y-5" data-testid="itops-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><Pulse size={26} className="text-orange-600" />IT Operations</h1>
        <p className="text-sm text-slate-500">Connector health, job monitoring, failure queues and pending work - refreshed every 30s.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Failed transactions" value={data.failures.length} tone={data.failures.length ? 'text-rose-600' : ''} testid="itops-stat-failures" />
        <Stat label="Leave approvals pending" value={p.leave_approvals ?? 0} testid="itops-stat-leaves" />
        <Stat label="Marks awaiting moderation" value={p.marks_moderation ?? 0} testid="itops-stat-moderation" />
        <Stat label="Open ITSM tickets" value={p.itsm_open ?? 0} testid="itops-stat-itsm" />
      </div>

      {data.alerts.length > 0 && (
        <div className="bg-white border border-rose-200 rounded-xl p-5" data-testid="itops-alerts">
          <p className="font-display font-bold mb-2 flex items-center gap-1.5 text-rose-600"><Warning size={16} weight="fill" />System alerts</p>
          {data.alerts.map((a, i) => (
            <div key={a.key || i} className="flex items-center gap-2 py-1.5 border-t border-slate-50">
              <p className="text-sm text-slate-700 flex-1">{a.message}</p>
              {canTicket && a.key && <AlertActions alert={a} connectors={connectors} tickets={tickets}
                onCreated={() => { qc.invalidateQueries({ queryKey: ['itsm-tickets'] }); qc.invalidateQueries({ queryKey: ['itops'] }); }} />}
            </div>
          ))}
        </div>)}

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="itops-connectors">
          <p className="font-display font-bold mb-2">Connector health</p>
          {data.connectors.map((c, i) => (
            <div key={i} className="flex items-center gap-2.5 py-2 border-t border-slate-50 text-sm">
              <Dot status={c.status} />
              <span className="font-medium">{c.name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{c.kind}</span>
              <span className="ms-auto text-xs text-slate-400 text-end max-w-[45%] truncate" title={c.detail}>{c.detail}</span>
            </div>))}
          {data.connectors.length === 0 && <p className="text-sm text-slate-400">No connectors configured yet.</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="itops-failures">
          <p className="font-display font-bold mb-2">Failed transactions</p>
          {data.failures.map((f, i) => (
            <div key={i} className="py-2 border-t border-slate-50 text-sm flex items-start gap-2">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 uppercase shrink-0 mt-0.5">{f.kind.replace('_', ' ')}</span>
              <span className="text-slate-700">{f.summary}<span className="block text-xs text-slate-400">{(f.at || '').slice(0, 16).replace('T', ' ')}</span></span>
              {f.retryable && <button data-testid={`itops-retry-${f.id}`} onClick={() => retryItsm(f.id)} className="ms-auto shrink-0 text-xs border border-slate-200 rounded px-2 py-1 hover:border-orange-500 flex items-center gap-1"><ArrowsClockwise size={12} />Retry</button>}
            </div>))}
          {data.failures.length === 0 && <p className="text-sm text-green-600 flex items-center gap-1.5"><CheckCircle size={16} weight="fill" />No failed transactions - everything is flowing.</p>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="itops-jobs">
        <p className="font-display font-bold mb-3">Job monitor</p>
        <DataGrid gridId="itops-jobs" rows={(data.jobs || []).map((j, i) => ({ ...j, id: `${j.job}-${i}` }))} exportName="job_runs"
          columns={[
            { key: 'job', label: 'Job' },
            { key: 'at', label: 'Ran at', render: (r) => (r.at || '').slice(0, 19).replace('T', ' ') },
            { key: 'ok', label: 'Outcome', render: (r) => r.ok ? <span className="text-green-600 text-xs font-semibold">OK</span> : <span className="text-rose-600 text-xs font-semibold">FAILED</span> },
            { key: 'detail', label: 'Detail' },
          ]}
          filters={[]} emptyText="No job runs recorded yet - crons and workflows will appear here as they execute." />
      </div>
    </div>
  );
}
